import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';

import {
  AngleDown,
  AutoComplete,
  ChakraFlex,
  ChakraMenu,
  ChakraMenuButton,
  ChakraMenuList,
  ChakraPortal,
  ChakraSimpleGrid,
  ChakraSpacer,
  ChakraSwitch,
  ChakraText,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  FAIconName,
  Filter,
  FireflyButton,
  MenuOption,
  MenuSelect,
  MenuSelectOption,
  Search,
  TextInput,
} from '@equip.health/ui';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';

import { NoteEvents } from '~/analytics';
import util from '~/components/common/calendar/util';
import {
  NoteStatus,
  PatientNoteFilterType,
} from '~/lib/constants/AddPatientNoteModal.constants';
import { AppointmentStatus } from '~/lib/constants/calendar.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { sortAndMapEnumerationToLabel } from '~/lib/util/enumerations';
import { toMenuSelectOption } from '~/lib/util/menu';
import {
  getProviderAutocompleteOption,
  getUserOption,
} from '~/lib/util/user.util';
import { userOptionAlphabeticalSortCompareFunction } from '~/lib/utils';
import {
  useGetAdminNoteTypesEnumQuery,
  useGetProviderTypesEnumQuery,
} from '~/store/services/enumerations';
import {
  useGetAllProvidersV2Query,
  useLazyGetAllProvidersV2Query,
} from '~/store/services/providers';
import { useGetAppointmentDetailsQuery } from '~/store/services/appointment';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

const statusOptions: MenuSelectOption[] = [
  { node: 'Draft', value: NoteStatus.DRAFT },
  { node: 'Signed', value: NoteStatus.SIGNED },
  { node: 'Void', value: NoteStatus.VOID },
];

const GET_PROVIDERS_PAGE_SIZE = 15 as const;

const { primary } = EquipUIFireflyV2Theme.colors;

const { label, h5, labelBold } = EquipUITheme.typography;

const TOP_LEVEL_ID = 'patient-notes-filter';

const NoteFilterEndIcon = () => (
  <AngleDown fill={primary[100]} fillOpacity={1} height="15px" width="12px" />
);

interface PatientNoteFilterProps {
  filterValue: PatientNoteFilterType;
  patientId: string;
  setFilterValue: (filterValue: PatientNoteFilterType) => void;
}

const PatientNoteFilter = ({
  filterValue,
  patientId,
  setFilterValue,
}: PatientNoteFilterProps) => {
  const { currentUser } = useGlobalContext();
  const { trackEvent } = useAnalytics();

  const [isOpen, setIsOpen] = useState(false);

  const [showMyNotes, setShowMyNotes] = useState<boolean>(
    filterValue?.showMyNotes ?? false,
  );
  const [showNotesWithAttachment, setShowNotesWithAttachment] =
    useState<boolean>(filterValue?.showNotesWithAttachment ?? false);

  const [status, setStatus] = useState<MenuSelectOption[]>(
    filterValue?.status ?? [],
  );
  const [creator, setCreator] = useState<AutoCompleteOption[]>(
    filterValue?.creator ?? [],
  );
  const [title, setTitle] = useState<string>(filterValue?.title ?? '');
  const [noteType, setNoteType] = useState<MenuSelectOption[]>(
    filterValue?.noteType ?? [],
  );
  const [creatorRole, setCreatorRole] = useState<MenuSelectOption[]>(
    filterValue?.creatorRole ?? [],
  );

  const [sessionLink, setSessionLink] = useState<MenuOption>(
    filterValue?.sessionLink ?? null,
  );

  const now = useMemo(() => DateTime.now(), []);
  const {
    data: patientAppointmentsData,
    error: patientAppointmentsError,
    isLoading: isPatientAppointmentsLoading,
  } = useGetAppointmentDetailsQuery(
    {
      patientId,
      startDateISO: now.plus({ days: 7 }).toISO(),
      endDateISO: now.minus({ days: 7 }).toISO(),
      statuses: [AppointmentStatus.COMPLETED, AppointmentStatus.SCHEDULED],
      pageSize: 100,
    },
    { skip: !patientId || !isOpen },
  );

  const {
    data: initialProviderData,
    isFetching: isGetInitialProvidersFetching,
  } = useGetAllProvidersV2Query(
    {
      'page-size': GET_PROVIDERS_PAGE_SIZE,
      'page-number': 1,
    },
    { skip: !isOpen },
  );

  const [
    getCreatorSearchProviders,
    { data: creatorProviders, isFetching: isGetCreatorProvidersFetching },
  ] = useLazyGetAllProvidersV2Query();

  const selfOption = {
    ...getUserOption(true)(currentUser),
    isMe: true,
  };

  const userOptions: AutoCompleteOption[] = useMemo(() => {
    const providers = creatorProviders?.data || initialProviderData?.data || [];
    return [
      ...(selfOption ? [selfOption] : []),
      ...providers
        .filter(
          ({ userId }: GetProviderByIdResponse) =>
            userId !== currentUser.userId,
        )
        .map((provider) => getProviderAutocompleteOption(provider))
        .sort(userOptionAlphabeticalSortCompareFunction),
    ];
  }, [creatorProviders, initialProviderData, currentUser.userId]);

  useEffect(() => {
    setShowMyNotes(filterValue?.showMyNotes ?? false);
    setShowNotesWithAttachment(filterValue?.showNotesWithAttachment ?? false);
    setCreatorRole(filterValue?.creatorRole ?? []);
    setCreator(filterValue?.creator ?? []);
    setNoteType(filterValue?.noteType ?? []);
    setStatus(filterValue?.status ?? []);
    setTitle(filterValue?.title ?? '');
    setSessionLink(filterValue?.sessionLink ?? null);
  }, [filterValue]);

  const { data: adminNoteTypes, isLoading: isGetAdminNoteTypesLoading } =
    useGetAdminNoteTypesEnumQuery();

  const { data: unsortedProviderTypes, isLoading: isGetProviderTypesLoading } =
    useGetProviderTypesEnumQuery();

  const providerTypes = useMemo<string[]>(
    () => sortAndMapEnumerationToLabel(unsortedProviderTypes ?? []),
    [unsortedProviderTypes],
  );

  const noteTypeOptions = useMemo<MenuSelectOption[]>(
    () => (adminNoteTypes ?? []).map(toMenuSelectOption),
    [adminNoteTypes],
  );

  const rolesMenuSelectOptions = useMemo<MenuSelectOption[]>(
    () => providerTypes.map(toMenuSelectOption),
    [providerTypes],
  );
  // Appointments list
  const sessionDetailsList = useMemo<MenuOption[]>(() => {
    return (patientAppointmentsData?.data ?? []).map((appointmentData) =>
      util.convertAppointmentDataToMenuItem(appointmentData),
    );
  }, [patientAppointmentsData]);

  // appointment data ?
  const noAppointmentsData =
    (isEmpty(sessionDetailsList) || !isEmpty(patientAppointmentsError)) &&
    !isPatientAppointmentsLoading;

  return (
    <ChakraFlex
      alignItems="center"
      flexWrap="wrap"
      justifyContent="space-between"
      paddingTop="24px"
    >
      <ChakraText {...h5} id={`${TOP_LEVEL_ID}__notes-label`}>
        Patient Notes
      </ChakraText>
      <ChakraSpacer />
      <ChakraMenu
        closeOnBlur
        onOpen={() => {
          trackEvent(NoteEvents.filterNoteTabButtonClicked, {
            Source: AnalyticsSourceValues.NOTES_TAB,
          });
          setIsOpen(true);
        }}
        onClose={() => {
          setIsOpen(false);
        }}
      >
        {({ onClose }) => (
          <>
            <ChakraMenuButton>
              <ChakraFlex alignItems="center" height="42px">
                <ChakraText
                  id={`${TOP_LEVEL_ID}__filter-label`}
                  {...labelBold}
                  color="primary.100"
                  marginRight="6px"
                >
                  Filter
                </ChakraText>
                <Filter fill={primary[100]} height="12px" width="12px" />
              </ChakraFlex>
            </ChakraMenuButton>
            <ChakraPortal>
              <ChakraMenuList
                height="700px"
                // TODO removable calc(), ensures buttons show up on small screen sizes - kind of an edge case?
                // position:fixed could solve maybe?
                maxHeight="calc(100vh - 140px)"
                overflowY="auto"
                padding="24px 16px"
                width="400px"
              >
                <ChakraSimpleGrid
                  flexDir="column"
                  paddingBottom="120px"
                  spacing="16px"
                >
                  <MenuSelect
                    endIcon={<NoteFilterEndIcon />}
                    formLabel="Creator role"
                    id={`${TOP_LEVEL_ID}__provider-type`}
                    isDisabled={isGetProviderTypesLoading}
                    isLoading={isGetProviderTypesLoading}
                    listMaxHeight="220px"
                    multiple
                    onSelect={(selectedValue: MenuSelectOption[]) =>
                      setCreatorRole(selectedValue)
                    }
                    options={(rolesMenuSelectOptions ?? []).filter(
                      ({ node }) =>
                        !status?.some(
                          ({ node: selectedNode }) => selectedNode === node,
                        ),
                    )}
                    placeholder="Creator role"
                    selectedValues={creatorRole || []}
                  />
                  <AutoComplete
                    endIcon={
                      <Search
                        fill={primary[100]}
                        fillOpacity={1}
                        height="20px"
                        width="20px"
                      />
                    }
                    fetchOptions={(searchTerm) => {
                      getCreatorSearchProviders({
                        'page-number': 1,
                        'page-size': GET_PROVIDERS_PAGE_SIZE,
                        'provider-search': searchTerm,
                        'include-email-search': false,
                      });
                    }}
                    id={`${TOP_LEVEL_ID}__creator`}
                    isDebounced
                    isLoading={
                      isGetInitialProvidersFetching ||
                      isGetCreatorProvidersFetching
                    }
                    multiple
                    numOptionsNeverFilterOut={1}
                    onRemove={(data: AutoCompleteOption) => {
                      setCreator(
                        (creator || [])?.filter(
                          ({ value }) => value !== data.value,
                        ),
                      );
                    }}
                    onSelect={(data: AutoCompleteOption) => {
                      setCreator((creator || [])?.concat(data));
                    }}
                    options={userOptions}
                    placeholder="Search"
                    selected={creator ?? []}
                    title="Creator"
                    toggleListOnFocus
                  />

                  <MenuSelect
                    endIcon={<NoteFilterEndIcon />}
                    formLabel="Note type"
                    id={`${TOP_LEVEL_ID}__note-type`}
                    isDisabled={isGetAdminNoteTypesLoading}
                    isLoading={isGetAdminNoteTypesLoading}
                    listMaxHeight="220px"
                    multiple
                    onSelect={(selectedValue: MenuSelectOption[]) =>
                      setNoteType(selectedValue)
                    }
                    options={(noteTypeOptions ?? []).filter(
                      ({ node }) =>
                        !status?.some(
                          ({ node: selectedNode }) => selectedNode === node,
                        ),
                    )}
                    placeholder="Note type"
                    selectedValues={noteType || []}
                  />
                  <MenuSelect
                    endIcon={<NoteFilterEndIcon />}
                    formLabel="Status"
                    id={`${TOP_LEVEL_ID}__status`}
                    listMaxHeight="220px"
                    multiple
                    onSelect={(selectedValue: MenuSelectOption[]) => {
                      setStatus(selectedValue);
                    }}
                    options={statusOptions?.filter(
                      ({ node }) =>
                        !status?.some(
                          ({ node: selectedNode }) => selectedNode === node,
                        ),
                    )}
                    placeholder="Status"
                    selectedValues={status || []}
                  />

                  <TextInput
                    id="patient-notes-filter__title"
                    label="Title"
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      event.stopPropagation();
                      setTitle(event.target.value);
                    }}
                    value={title}
                  />
                  {/* Session Link filter */}
                  <MenuSelect
                    endIcon={<NoteFilterEndIcon />}
                    formLabel="Session Link"
                    formMarginBottom="16px"
                    id={`${TOP_LEVEL_ID}__session-link`}
                    isClearable
                    isDisabled={noAppointmentsData}
                    isLoading={isPatientAppointmentsLoading}
                    listMaxHeight="220px"
                    menuWidth="365px"
                    onSelect={(selectedValue: MenuOption) => {
                      setSessionLink(selectedValue);
                    }}
                    options={sessionDetailsList}
                    placeholder={
                      noAppointmentsData ? 'No Appointments' : 'Session Link'
                    }
                    selectedValues={sessionLink || null}
                  />
                  <ChakraFlex paddingLeft="4px">
                    <ChakraSwitch
                      colorScheme="teal"
                      id={`${TOP_LEVEL_ID}__notes-with-attachments__switch`}
                      isChecked={showNotesWithAttachment}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setShowNotesWithAttachment(event.target.checked);
                      }}
                    />
                    <ChakraText
                      _active={{ cursor: 'grabbing' }}
                      {...label}
                      id={`${TOP_LEVEL_ID}__notes-with-attachments__text`}
                      marginLeft="8px"
                    >
                      Notes with attachments
                    </ChakraText>
                  </ChakraFlex>
                  <ChakraFlex paddingLeft="4px">
                    <ChakraSwitch
                      colorScheme="teal"
                      id={`${TOP_LEVEL_ID}__only-my-notes__switch`}
                      isChecked={showMyNotes}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setShowMyNotes(event.target.checked);
                      }}
                    />
                    <ChakraText
                      _active={{ cursor: 'grabbing' }}
                      {...label}
                      id={`${TOP_LEVEL_ID}__only-my-notes__text`}
                      marginLeft="8px"
                    >
                      Only my notes
                    </ChakraText>
                  </ChakraFlex>
                </ChakraSimpleGrid>
                <ChakraFlex
                  flexDir="column"
                  background="neutral.background.primary"
                  bottom="1px"
                  padding="12px 0"
                  position="absolute"
                  width="352px"
                  gap="6px"
                >
                  <FireflyButton
                    id={`${TOP_LEVEL_ID}__reset`}
                    onClick={() => {
                      setFilterValue(null);
                      onClose();
                    }}
                    rightIcon={FAIconName.redoRegular}
                    variant="tertiary"
                    isFullWidth
                  >
                    Reset
                  </FireflyButton>
                  <FireflyButton
                    id={`${TOP_LEVEL_ID}__apply`}
                    onClick={() => {
                      setFilterValue({
                        creator,
                        creatorRole,
                        noteType,
                        sessionLink,
                        showMyNotes,
                        showNotesWithAttachment,
                        status,
                        title,
                      });
                      onClose();
                    }}
                    rightIcon={FAIconName.checkRegular}
                    isFullWidth
                  >
                    Apply
                  </FireflyButton>
                </ChakraFlex>
              </ChakraMenuList>
            </ChakraPortal>
          </>
        )}
      </ChakraMenu>
    </ChakraFlex>
  );
};

export default React.memo(PatientNoteFilter);
